import { replicateSupabase } from './replicateSupabase';
import { supabase } from '../../lib/supabase';
import type { DepilawayDatabase } from '../database';
import { useSyncStore } from '../../stores/sync.store';
import { pendingPulls } from './syncState';
import type { Subscription } from 'rxjs';

// ── Singleton State Machine ──────────────────────────────────────────────
type SyncStatus = 'IDLE' | 'INITIALIZING' | 'RUNNING';

let syncStatus: SyncStatus = 'IDLE';
let currentReplicatedDb: DepilawayDatabase | null = null;

const activeReplications: any[] = [];
const activeSubscriptions: Subscription[] = [];
let activeSharedChannel: any = null;

let isManualSyncActive = false;
export function isManualSyncing(): boolean {
  return isManualSyncActive;
}

// ── Throttling & Cooldown Configuration ──────────────────────────────────
let lastGlobalReSyncTime = 0;
const GLOBAL_RESYNC_COOLDOWN_MS = 10000;
let reSyncDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Re-sincronización en segundo plano con throttling.
 */
export function debouncedReSyncAll(reason: string = 'manual') {
  const syncStore = useSyncStore.getState();

  if (!syncStore.navigatorOnline) {
    console.log(`[Sync] Sin conexión física a internet. Omitiendo resync. Motivo: ${reason}`);
    return;
  }

  if (!syncStore.autoSyncEnabled && reason !== 'manual') {
    console.log(`[Sync] Auto-sincronización pausada por el usuario. Motivo: ${reason}`);
    return;
  }

  const now = Date.now();
  const timeSinceLastSync = now - lastGlobalReSyncTime;

  if (reason !== 'manual' && timeSinceLastSync < GLOBAL_RESYNC_COOLDOWN_MS) {
    return;
  }

  if (reSyncDebounceTimer) clearTimeout(reSyncDebounceTimer);

  reSyncDebounceTimer = setTimeout(() => {
    lastGlobalReSyncTime = Date.now();
    console.log(`[Sync] Ejecutando reSync en segundo plano. Motivo: ${reason}`);
    
    activeReplications.forEach((rep) => {
      try {
        rep.reSync();
      } catch (err) {
        console.warn('[Sync] Error al re-sincronizar colección:', err);
      }
    });
  }, reason === 'manual' ? 0 : 500);
}

// ── Rastreador Local Continuo de Cambios Pendientes ─────────────────────
let trackerSubscriptions: Subscription[] = [];
let pendingDebounce: ReturnType<typeof setTimeout> | null = null;

export function setupPendingTracker(db: DepilawayDatabase) {
  if (trackerSubscriptions.length > 0) return;

  try {
    const pendingQuery = (db as any).sync_queue.find();
    const sub = pendingQuery.$.subscribe(() => {
      if (pendingDebounce) clearTimeout(pendingDebounce);
      pendingDebounce = setTimeout(() => {
        updateTotalPendingCount(db);
      }, 500);
    });
    trackerSubscriptions.push(sub);
  } catch (err) {
    console.warn(`[Sync] Error configurando rastreador para sync_queue:`, err);
  }

  // Ejecución inicial para sincronizar el status
  updateTotalPendingCount(db);
}

/**
 * Inicialización Singleton de Replicación Supabase <-> RxDB en tiempo real.
 */
export function setupReplication(db: DepilawayDatabase) {
  if (!supabase) {
    console.warn('[Sync] Supabase no configurado. Modo local is_active.');
    return;
  }

  const syncStore = useSyncStore.getState();
  if (!syncStore.autoSyncEnabled) {
    console.log('[Sync] Auto-sincronización desactivada. Permaneciendo en modo local.');
    return;
  }

  // Inicializar siempre el rastreador local de pendientes
  setupPendingTracker(db);

  if (syncStatus === 'RUNNING' && currentReplicatedDb === db && activeReplications.length > 0) {
    return;
  }

  if (syncStatus === 'INITIALIZING') return;

  syncStatus = 'INITIALIZING';

  if (currentReplicatedDb !== db) {
    cleanupReplication();
  }

  currentReplicatedDb = db;
  const sharedChannel = supabase.channel('db-realtime-global-sync');
  activeSharedChannel = sharedChannel;

  const collectionsToSync = [
    // Nivel 0: Raíces sin dependencias FK
    { key: 'app_config', table: 'app_config' },
    { key: 'exchange_rates', table: 'exchange_rates' },
    { key: 'branches', table: 'branches' },
    { key: 'user_profiles', table: 'user_profiles' },
    // Nivel 1: Catálogos hijos de branches
    { key: 'categories', table: 'categories' },
    { key: 'payment_methods', table: 'payment_methods' },
    { key: 'service_categories', table: 'service_categories' },
    { key: 'staff', table: 'staff' },
    { key: 'customers', table: 'customers' },
    { key: 'packages', table: 'packages' },
    // Nivel 2: Depende de service_categories
    { key: 'services', table: 'services' },
    // Nivel 3: Operaciones que dependen de customers/branches
    { key: 'transactions', table: 'transactions' },
    // Nivel 4: Depende de customers, packages y transactions
    { key: 'customer_packages', table: 'customer_packages' },
  ] as const;



  const activeSyncs = new Set<string>();

  for (const item of collectionsToSync) {
    const collection = db[item.key];

    try {
      const repState = replicateSupabase({
        collection,
        client: supabase,
        tableName: item.table,
        replicationIdentifier: `supabase-sync-${item.key}`,
        modifiedField: 'updated_at',
        deletedField: 'is_deleted',
        live: true,
        sharedChannel,
        pull: {
          batchSize: 100,
          queryBuilder: item.key === 'exchange_rates' ? ({ query }: any) => {
            let startYear = String(new Date().getFullYear());
            try {
              const progress = localStorage.getItem('depilaway-setup-progress');
              if (progress) {
                const parsed = JSON.parse(progress);
                if (parsed.startYear) startYear = String(parsed.startYear);
              }
            } catch (e) {}
            return query.gte('until', `${startYear}-01-01`);
          } : undefined,
          modifier: (doc: any) => {
            const clean = { ...doc };
            return clean;
          }
        },
        push: {
          batchSize: 50,
          modifier: (doc: any) => {
            const clean = { ...doc };
            return clean;
          }
        },
      });

      activeReplications.push(repState);

      const activeSub = repState.active$.subscribe((isActive: boolean) => {
        if (isActive) {
          activeSyncs.add(item.key);
        } else {
          activeSyncs.delete(item.key);
        }
        
        const isCurrentlySyncing = activeSyncs.size > 0;
        syncStore.setSyncing(isCurrentlySyncing);
        if (!isCurrentlySyncing) {
          if (useSyncStore.getState().errors.length === 0) {
            syncStore.setLastSyncAt(Date.now());
          }
          isManualSyncActive = false;
        }
      });
      activeSubscriptions.push(activeSub);

      const errorSub = repState.error$.subscribe((err: any) => {
        if (err?.message && String(err.message).includes('SyncPaused')) return;
        console.error(`[Sync] Error replicando ${item.key}:`, err);
        syncStore.addError({
          message: err.message || 'Error de sincronización desconocido',
          collection: item.key,
        });
      });
      activeSubscriptions.push(errorSub);

      const sentSub = repState.sent$.subscribe(async (event: any) => {
        const doc = event.newDocumentState || event;
        const docId = doc?.id;
        if (!docId) return;
        try {
          const query = (db as any).sync_queue.findOne({ selector: { id: docId } });
          await query.remove();
        } catch (err) {
          console.warn(`[Sync] Failed to remove local doc ${docId} from sync_queue:`, err);
        }
      });
      activeSubscriptions.push(sentSub);

    } catch (err: any) {
      console.error(`[Sync] Failed to initialize replication for ${item.key}:`, err);
      syncStore.addError({
        message: err.message || 'Fallo de inicialización de sincronización',
        collection: item.key,
      });
    }
  }

  lastGlobalReSyncTime = Date.now();

  sharedChannel.subscribe((status: string) => {
    if (status === 'SUBSCRIBED') {
      console.log('[Sync] Shared Supabase Realtime channel established.');
      debouncedReSyncAll('realtime_channel_connected');
    }
  });

  syncStatus = 'RUNNING';
  console.log('[Sync] Replicación inicializada exitosamente (Estado: RUNNING).');
}

/**
 * Ejecució 1-Shot Directa de Sincronización Manual (Push & Pull).
 * Funciona garantizadamente tanto con auto-sync encendido como apagado.
 */
export async function runManualSync(db: DepilawayDatabase) {
  const syncStore = useSyncStore.getState();

  if (!syncStore.navigatorOnline) {
    console.warn('[Sync Manual] Sin conexión a internet.');
    return;
  }

  if (!supabase) {
    console.warn('[Sync Manual] Supabase no configurado.');
    return;
  }

  if (syncStore.isSyncing) {
    console.log('[Sync Manual] Sincronización en curso. Omitiendo.');
    return;
  }

  let hasErrors = false;

  try {
    syncStore.setSyncing(true);
    isManualSyncActive = true;
    console.log('[Sync Manual] Iniciando ciclo directo 1-Shot de sincronización...');

    const collectionsToSync: Array<{ key: keyof DepilawayDatabase; table: string }> = [
      // Nivel 0: Raíces sin dependencias FK
      { key: 'app_config', table: 'app_config' },
      { key: 'exchange_rates', table: 'exchange_rates' },
      { key: 'branches', table: 'branches' },
      { key: 'user_profiles', table: 'user_profiles' },
      // Nivel 1: Catálogos hijos de branches
      { key: 'categories', table: 'categories' },
      { key: 'payment_methods', table: 'payment_methods' },
      { key: 'service_categories', table: 'service_categories' },
      { key: 'staff', table: 'staff' },
      { key: 'customers', table: 'customers' },
      { key: 'packages', table: 'packages' },
      // Nivel 2: Depende de service_categories
      { key: 'services', table: 'services' },
      // Nivel 3: Operaciones que dependen de customers/branches
      { key: 'transactions', table: 'transactions' },
      // Nivel 4: Depende de customers, packages y transactions
      { key: 'customer_packages', table: 'customer_packages' },
    ];

    // Si la réplica en tiempo real está activa, disparar reSync directo
    if (activeReplications.length > 0) {
      activeReplications.forEach((rep) => {
        try { rep.reSync(); } catch (e) {}
      });
      // Breve pausa para permitir que los streams procesen
      await new Promise((r) => setTimeout(r, 1200));
    }

    // Proceso 1-shot explícito para garantizar push de registros pendientes
    const queueDocs = await (db as any).sync_queue.find().exec();
    const queuedByCollection: Record<string, any[]> = {};
    
    for (const qDoc of queueDocs) {
      const data = qDoc.toJSON ? qDoc.toJSON() : qDoc;
      if (!queuedByCollection[data.collection_name]) {
        queuedByCollection[data.collection_name] = [];
      }
      queuedByCollection[data.collection_name].push(qDoc);
    }

    for (const item of collectionsToSync) {
      const queueItemsForCol = queuedByCollection[item.key];
      if (!queueItemsForCol || queueItemsForCol.length === 0) continue;
      
      const collectionObj = db[item.key] as any;
      if (!collectionObj) continue;

      const schemaProps = collectionObj.schema.jsonSchema.properties || {};
      const allowedKeys = new Set(Object.keys(schemaProps));
      const primaryPath = collectionObj.schema.primaryPath || 'id';

      const rowsToPush: any[] = [];
      const validQueueDocs: any[] = [];

      for (const qDoc of queueItemsForCol) {
        const qData = qDoc.toJSON ? qDoc.toJSON() : qDoc;
        const targetId = qData.id;
        
        const docObj = await collectionObj.findOne({ selector: { [primaryPath]: targetId } }).exec();
        
        if (docObj) {
          const raw = docObj.toJSON ? docObj.toJSON() : docObj;
          const cleanDoc: any = {};
          for (const k of Object.keys(raw)) {
            if (allowedKeys.has(k) && k !== 'sync_state') {
              cleanDoc[k] = raw[k];
            }
          }
          cleanDoc.is_deleted = Boolean(raw.is_deleted);
          rowsToPush.push(cleanDoc);
          validQueueDocs.push(qDoc);
        } else {
          await qDoc.remove();
        }
      }

      if (rowsToPush.length > 0) {
        const { error } = await supabase
          .from(item.table)
          .upsert(rowsToPush, { onConflict: primaryPath });

        if (!error) {
          for (const qDoc of validQueueDocs) {
             await qDoc.remove();
          }
          console.log(`[Sync Manual] ${rowsToPush.length} registros subidos a ${item.table}.`);
        } else {
          hasErrors = true;
          console.error(`[Sync Manual] Error subiendo a ${item.table}:`, error);
          syncStore.addError({
            message: error.message || `Error al subir cambios a ${item.table}`,
            collection: item.key,
          });
        }
      }
    }

    // Proceso 1-shot de Pull para descargar datos recientes de la nube
    for (const item of collectionsToSync) {
      const collectionObj = db[item.key] as any;
      if (!collectionObj) continue;

      const { data, error } = await supabase
        .from(item.table)
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(100);

      if (error) {
        hasErrors = true;
        console.error(`[Sync Manual] Error descargando de ${item.table}:`, error);
        syncStore.addError({
          message: error.message || `Error al descargar ${item.table}`,
          collection: item.key,
        });
      } else if (data && data.length > 0) {
        const primaryPath = collectionObj.schema.primaryPath || 'id';
        for (const row of data) {
          const docId = row[primaryPath];
          if (!docId) continue;

          pendingPulls.add(docId);
          setTimeout(() => pendingPulls.delete(docId), 10000);

          const existing = await collectionObj.findOne(docId).exec();
          if (!existing) {
            const cleanRow = { ...row };
            delete cleanRow.sync_state_cloud;
            try {
              await collectionObj.insert(cleanRow);
              // Ensure sync_queue does not hold this cloud doc
              try {
                const qDoc = await (db as any).sync_queue.findOne({ selector: { id: docId } }).exec();
                if (qDoc) await qDoc.remove();
              } catch (e) {}
            } catch (err: any) {
              hasErrors = true;
              console.warn(`[Sync Manual] Error al insertar localmente en ${item.key}:`, err);
              syncStore.addError({
                message: `Error al guardar localmente en ${item.key}: ${err?.message || ''}`,
                collection: item.key,
              });
            }
          } else {
            const localData = existing.toJSON();
            // Solo actualizar local si el document local no tiene cambios pendientes de subir
            const isPending = await (db as any).sync_queue.findOne({ selector: { id: docId, collection_name: item.key } }).exec();
            if (!isPending && row.updated_at >= localData.updated_at) {
              const cleanRow = { ...row };
              try {
                await existing.patch(cleanRow);
              } catch (err: any) {
                hasErrors = true;
                console.warn(`[Sync Manual] Error al actualizar localmente en ${item.key}:`, err);
                syncStore.addError({
                  message: `Error al actualizar localmente en ${item.key}: ${err?.message || ''}`,
                  collection: item.key,
                });
              }
            }
          }
        }
      }
    }

    if (!hasErrors && useSyncStore.getState().errors.length === 0) {
      syncStore.setLastSyncAt(Date.now());
    }
    await updateTotalPendingCount(db);
  } catch (err: any) {
    console.error('[Sync Manual] Excepción durante sincronización:', err);
    syncStore.addError({
      message: err?.message || 'Error general en sincronización manual',
      collection: 'all',
    });
  } finally {
    isManualSyncActive = false;
    syncStore.setSyncing(false);
  }
}

let syncUpdateTimeout: any = null;

async function updateTotalPendingCount(dbInst: any) {
  if (syncUpdateTimeout) clearTimeout(syncUpdateTimeout);
  
  syncUpdateTimeout = setTimeout(async () => {
    const db = dbInst as any;
    const pendingItems: Array<import('../../stores/sync.store').PendingItem> = [];
    
    try {
      if (!db.sync_queue) return;
      
      const docs = await db.sync_queue.find().exec();
      
      for (const doc of docs) {
        const docData = doc.toJSON ? doc.toJSON() : doc;
        pendingItems.push({
          id: docData.id,
          collection: docData.collection_name || 'unknown',
          collectionLabel: docData.collection_name || 'Desconocido',
          label: `Pendiente: ${docData.collection_name || 'Item'} (${docData.action || 'Sync'})`,
          action: docData.action || 'update',
        });
      }
      
      const syncStore = useSyncStore.getState();
      syncStore.setPendingItems(pendingItems);
    } catch (err) {
      console.error('[Sync] Error contando documentos pendientes:', err);
    }
  }, 200);
}

/**
 * Limpieza total de replicación.
 */
export function cleanupReplication() {
  console.log('[Sync] Limpiando replicación en segundo plano...');
  currentReplicatedDb = null;
  lastGlobalReSyncTime = 0;

  if (reSyncDebounceTimer) {
    clearTimeout(reSyncDebounceTimer);
    reSyncDebounceTimer = null;
  }

  if (syncUpdateTimeout) {
    clearTimeout(syncUpdateTimeout);
    syncUpdateTimeout = null;
  }

  if (activeSharedChannel) {
    try {
      supabase.removeChannel(activeSharedChannel);
    } catch (err) {
      console.error('[Sync] Error removiendo canal realtime:', err);
    }
    activeSharedChannel = null;
  }

  while (activeSubscriptions.length > 0) {
    const sub = activeSubscriptions.pop();
    if (sub) {
      try { sub.unsubscribe(); } catch (err) {}
    }
  }

  while (activeReplications.length > 0) {
    const rep = activeReplications.pop();
    if (rep) {
      try { rep.cancel(); } catch (err) {}
    }
  }

  syncStatus = 'IDLE';
  console.log('[Sync] Replicación en segundo plano detenida.');
}
